import { useState, useMemo } from 'react';
import { useAuth } from 'wasp/client/auth';
import { useNavigate, Link } from 'react-router-dom';
import { isAdministrator } from '../../client/lib/platformRole';
import { useQuery, useAction } from 'wasp/client/operations';
import { getFieldConfigs, deleteFieldConfig } from 'wasp/client/operations';
import { Plus, Search, Edit, Trash2, Settings } from 'lucide-react';
import LoadingSpinner from '../layout/LoadingSpinner';
import { EnhancedActionButton } from '../../components/ui/enhanced-page-layout';
import { TableFilterLayout } from '../../components/ui/table-filter-layout';
import { useFixedPageStack } from '../../client/hooks/useFixedPageStack';

export default function FieldConfigsPage() {
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const deleteFieldConfigFn = useAction(deleteFieldConfig);

  if (!user) {
    return <div>Loading...</div>;
  }

  if (!isAdministrator(user)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600">You must be an admin to access this page.</p>
        </div>
      </div>
    );
  }

  const { data: fieldConfigs = [], isLoading, error } = useQuery(getFieldConfigs, {
    includeInactive: true,
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [filterContentType, setFilterContentType] = useState('all');
  const [filterFieldType, setFilterFieldType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const filteredData = useMemo(() => {
    return (fieldConfigs as any[]).filter((config: any) => {
      if (
        searchTerm &&
        !config.label.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !config.fieldKey.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        return false;
      }
      if (filterContentType !== 'all' && config.contentType !== filterContentType) return false;
      if (filterFieldType !== 'all' && config.fieldType !== filterFieldType) return false;
      if (filterStatus === 'active' && !config.isActive) return false;
      if (filterStatus === 'inactive' && config.isActive) return false;
      return true;
    });
  }, [fieldConfigs, searchTerm, filterContentType, filterFieldType, filterStatus]);

  const sortedData = useMemo(() => {
    return [...filteredData].sort((a: any, b: any) => {
      // Sort by contentType first, then displayOrder
      if (a.contentType !== b.contentType) {
        return a.contentType.localeCompare(b.contentType);
      }
      return a.displayOrder - b.displayOrder;
    });
  }, [filteredData]);

  const uniqueContentTypes = useMemo(() => {
    const types = new Set((fieldConfigs as any[]).map((c: any) => c.contentType));
    return Array.from(types).sort();
  }, [fieldConfigs]);

  const uniqueFieldTypes = useMemo(() => {
    const types = new Set((fieldConfigs as any[]).map((c: any) => c.fieldType));
    return Array.from(types).sort();
  }, [fieldConfigs]);

  const handleDelete = async (id: string, label: string) => {
    if (window.confirm(`Are you sure you want to delete the field configuration "${label}"?`)) {
      try {
        await deleteFieldConfigFn({ id });
      } catch (error) {
        console.error('Error deleting field config:', error);
        alert('Error deleting field configuration. Please try again.');
      }
    }
  };

  const hasData = sortedData.length > 0;
  const fixedStack = useFixedPageStack({
    secondaryKind: hasData ? 'table-filter' : 'none',
    secondaryRefDeps: [hasData, searchTerm, filterContentType, filterFieldType, filterStatus],
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Field Configurations</h2>
          <p className="text-gray-600">{error.message || 'An error occurred'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={fixedStack.pageContainerClassName} style={fixedStack.pageContainerStyle}>
        {/* Fixed Header */}
        <div
          ref={fixedStack.pageHeaderRef}
          className="fixed top-[var(--header-height)] left-[var(--sidebar-width)] right-0 z-30 bg-white dark:bg-sidebar-active transition-colors border-b border-gray-200 dark:border-gray-700"
        >
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Field Configurations
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Manage dynamic form fields for content types ({sortedData.length} total)
                </p>
              </div>
              <div className="flex items-center gap-3">
                <EnhancedActionButton
                  onClick={() => navigate('/admin/field-configs/new')}
                  icon={Plus}
                  label="Create Field Config"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table Header with Filters */}
        {hasData && (
          <TableFilterLayout
            className={`fixed ${fixedStack.secondaryFixedTopClass} left-[var(--sidebar-width)] right-0 z-20`}
            onHeightChange={fixedStack.onTableFilterHeightChange}
            columns={[
              { title: 'Label', span: 3 },
              { title: 'Field Key', span: 2 },
              { title: 'Content Type', span: 2 },
              { title: 'Field Type', span: 2 },
              { title: 'Status', span: 1 },
              { title: 'Order', span: 1 },
              { title: 'Actions', span: 1 },
            ]}
            filters={[
              {
                span: 3,
                content: (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search label or key..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                ),
              },
              { span: 2, content: <div /> },
              {
                span: 2,
                content: (
                  <select
                    value={filterContentType}
                    onChange={(e) => setFilterContentType(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Types</option>
                    {uniqueContentTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                ),
              },
              {
                span: 2,
                content: (
                  <select
                    value={filterFieldType}
                    onChange={(e) => setFilterFieldType(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Field Types</option>
                    {uniqueFieldTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                ),
              },
              {
                span: 1,
                content: (
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                ),
              },
              { span: 2, content: <div /> },
            ]}
          />
        )}

        {/* Content Area */}
        <div
          className={fixedStack.scrollContainerClassName}
          style={fixedStack.scrollContainerStyle}
        >
          <div className="px-4">
            {sortedData.length === 0 ? (
              <div className="text-center py-12">
                <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No field configurations found
                </h3>
                <p className="text-gray-600 mb-6">
                  {searchTerm || filterContentType !== 'all' || filterFieldType !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Create your first field configuration to get started'}
                </p>
                {!searchTerm && filterContentType === 'all' && filterFieldType === 'all' && (
                  <EnhancedActionButton
                    onClick={() => navigate('/admin/field-configs/new')}
                    icon={Plus}
                    label="Create Field Config"
                  />
                )}
              </div>
            ) : (
              <div className="space-y-0">
                {sortedData.map((config: any) => (
                  <div
                    key={config.id}
                    className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50 dark:hover:bg-sidebar-hover border-b border-gray-200 dark:border-gray-700 transition-colors"
                  >
                    {/* Label */}
                    <div className="col-span-3">
                      <Link
                        to={`/admin/field-configs/${config.id}/edit`}
                        className="font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400"
                      >
                        {config.label}
                      </Link>
                      {config.isRequired && (
                        <span className="ml-2 text-xs text-red-600 dark:text-red-400">*</span>
                      )}
                    </div>

                    {/* Field Key */}
                    <div className="col-span-2 text-sm text-gray-600 dark:text-gray-300 font-mono">
                      {config.fieldKey}
                    </div>

                    {/* Content Type */}
                    <div className="col-span-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                        {config.contentType}
                      </span>
                    </div>

                    {/* Field Type */}
                    <div className="col-span-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        {config.fieldType}
                      </span>
                    </div>

                    {/* Status */}
                    <div className="col-span-1">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          config.isActive
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {config.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    {/* Display Order */}
                    <div className="col-span-1 text-sm text-gray-600 dark:text-gray-300 text-center">
                      {config.displayOrder}
                    </div>

                    {/* Actions */}
                    <div className="col-span-1 flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/admin/field-configs/${config.id}/edit`)}
                        className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(config.id, config.label)}
                        className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
  );
}



